import { ApiParameter, get, HasPagingParameters, patch, post, request } from "@insite/client-framework/Services/ApiService";
import { AttributeTypeFacetModel, BaseModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";

const apiUri = "/api/v2/dana/credits";

export interface GetCreditMemosApiParameter extends ApiParameter, HasPagingParameters {
  creditMemoNumber: string;
  primaryPONumber: string;
  secondaryPoNumber: string;
  partNumber: string;
  billTo: string;
  creditMemoTotal: string;
  documentType: string;
  fromDate?: string; // ISO date string, e.g., "2025-09-08"
  poNumber: string;
  salesOrganization: string;
  toDate?: string;
  sortBy?: string;
  orderType?: string;
}
export interface GetCreditMemoDetailsApiParameter extends ApiParameter, HasPagingParameters {
  creditMemoNumber: string;

}

export interface GetShareCreditMemoParameter extends ApiParameter {
  shareEmails?: string[];
  creditMemoNumber: string;
}
export interface CreditMemo {
  billTo: string;
  creditClear: number;
  creditClearedDate?: string; // ISO date string
  creditMemoAmount: number;
  appliedCreditsDisplay: string;
  creditMemoAmountDisplay: string;
  openCreditsDisplay: string;
  creditMemoDate?: string;
  creditMemoNumber: string;
  customerPartNumber: string;
  invoiceDate?: string;
  invoiceNumber: string;
  invoiceType: string;
  invoiceTypeDescription: string;
  originalInvoiceNumber: string;
  originalPONumber: string;
  partNumbers: string[];
  partQuantity: number;
  reservedfield1: string;
  reservedfield2: string;
  reservedfield3: string;
  secondaryPONumber: string;
  appliedCredits: number;
  openCredits: number;
}

export interface CreditViewResult extends BaseModel {
  allCredits: CreditMemo[];
  credits: CreditMemo[];
  pagination: PaginationModel;
  creditsCount: number;
  appliedCreditAmount: number;
  openCreditAmount: number;
}
export interface ShareCreditResult extends BaseModel {
  success: boolean;
  message: string;
}
export interface CreditDetailsPriceInfo {
  amount: number;
  priceType: string;
}

export interface CreditDetailsInfo {
  branch: string;
  carrier: string;
  carrierName: string;
  countryOfOrigin: string;
  customerPartNumber: string;
  deliveryDate: string | null; // ISO date string
  deliveryNumber: string;
  discount: number;
  frCode: string;
  gyorInpPartNumber: string;
  hsNumber: string;
  identifier: string;
  invoiceDate: string | null;
  invoiceNumber: string;
  lastStatus: string;
  lineNumber: string;
  lineType: string;
  miscCharges: number;
  netAmount: number;
  nextStatusDescription: string;
  orderDate: string | null;
  orderNumber: string;
  orderType: string;
  partDescription: string;
  partDescription2: string;
  partNumber: string;
  partPriceInfo: CreditDetailsPriceInfo[];
  pickNumber: string;
  poNumber: string;
  pricePerUnit: number;
  quantityOrdered: number;
  quantityShipped: number;
  requestDate: string | null;
  trackingNumber: string;
  unitOfMeasure: string;
  weight: number;
  weightUnit: string;
}


export interface CreditMemoDetails {
  accountNumber: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  address5: string;
  billAddress1: string;
  billAddress2: string;
  billCity: string;
  billCountry: string;
  billName: string;
  billState: string;
  billTo: string;
  billZip: string;
  containerized: string;
  countryOfOrigin: string;
  creditMemoAmount: number;
  creditMemoDate: string | null;
  creditMemoNumber: string;
  csrCont: string;
  curr: string;
  currency: string;
  customerTaxNumber: string;
  customerVatEuroNumber: string;
  customerVatNumber: string;
  danaShpNumber: string;
  dateOfExport: string | null;
  deliveryDate: string | null;
  deliveryNumber: string;
  documentDate: string | null;
  documentNumber: string;
  estimatedTimeOfArrival: string | null;
  exporterAdd: string;
  exportingCarrier: string;
  forwardingAgentAdd: string;
  forwardingAgentAddress1: string;
  forwardingAgentAddress2: string;
  forwardingAgentCity: string;
  forwardingAgentCode: string;
  forwardingAgentCountry: string;
  forwardingAgentName: string;
  forwardingAgentState: string;
  forwardingAgentZip: string;
  freightAmount: number;
  incoterms: string;
  incotermsCode: string;
  incotermsDescription: string;
  invoiceDate: string | null;
  invoiceInfo: CreditDetailsInfo[];
  invoiceNumber: string;
  letterOfCredit: string;
  mailAddress1: string;
  mailAddress2: string;
  mailCity: string;
  mailCountry: string;
  mailName: string;
  mailState: string;
  mailTo: string;
  mailZip: string;
  modeOfTransport: string;
  originalInvoiceDate: string | null;
  originalInvoiceNumber: string;
  poNumber: string;
  portOfDestination: string;
  portOfExport: string;
  priceInfo: CreditDetailsPriceInfo[];
  processNumber: string;
  remarks: string;
  remitToAddress1: string;
  remitToAddress2: string;
  remitToCity: string;
  remitToCountry: string;
  remitToCustomerId: string;
  remitToName: string;
  remitToState: string;
  remitToZip: string;
  shipAddress1: string;
  shipAddress2: string;
  shipCity: string;
  shipCountry: string;
  shipName: string;
  shipPlantAddress1: string;
  shipPlantAddress2: string;
  shipPlantCity: string;
  shipPlantCode: string;
  shipPlantCountry: string;
  shipPlantName: string;
  shipPlantState: string;
  shipPlantZip: string;
  shipState: string;
  shipTo: string;
  shipZip: string;
  soldToAddress1: string;
  soldToAddress2: string;
  soldToCity: string;
  soldToCountry: string;
  soldToCustomerId: string;
  soldToName: string;
  soldToState: string;
  soldToZip: string;
  terms: string;
  termsDescription: string;
  totalTax: number;
  vat: number;
}
export interface SalseOrgDetails {
  orgName: string;
  orgAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  siteName: string;
}


export interface CreditMemoDetailsResult extends BaseModel {
  creditMemoDetails: CreditMemoDetails;
  salseOrgDetails: SalseOrgDetails;
}
export async function getCreditMemos(parameter: GetCreditMemosApiParameter) {
  const creditMemosResult = await get<CreditViewResult>(`${apiUri}/`, parameter);
  return creditMemosResult;
}
export async function getCreditMemoDetails(parameter: GetCreditMemoDetailsApiParameter) {
  const creditMemosResult = await get<any>(`${apiUri}/${parameter.creditMemoNumber}`, parameter);
  return creditMemosResult;
}
export function printCreditMemoPdf(creditMemoNumber: string) {
  const pdfWindow = window.open(`/api/creditpdf/GetPdf/${creditMemoNumber}`, "_blank");
  if (pdfWindow) {
    pdfWindow.onload = () => {
      pdfWindow.focus();
      pdfWindow.print();
    };
  }
}
export async function shareCreditMemoViaEmail(parameter: GetShareCreditMemoParameter) {

  try {

    const creditMemosResult = await patch<any>(`${apiUri}/ShareCreditMemo`, parameter);
    return creditMemosResult;

  } catch (error) {
    if ("status" in error && (error.status === 400 || error.status === 403 || error.status === 404)) {
      return {
        successful: false,
        errorMessage: error?.errorJson?.message || error?.errorMessage || "error",
        statusCode: error.status,
      };
    }
    throw error;
  }

}